import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { AddressProvider } from './contexts/AddressContext';
import { applyDirOnLoad } from './lib/i18n';
import { applyBrandOnLoad } from './lib/brand';
import './styles/globals.css';

// Set <html dir>/<html lang> before React paints so the first frame
// renders in the right direction (avoids a flash of LTR for Arabic users).
applyDirOnLoad();

// Same reason, for the brand: one deployment serves Luvelle and Taheel on
// different hostnames, and the title/description live in a single index.html.
// A Taheel customer was getting a tab that said Luvelle Home Spa.
applyBrandOnLoad();

// Light theme is canonical for the consumer web app (matches the Luvelle
// brand identity letterhead/business card/ID card treatments). The dark
// variant is reserved for the splash and premium booking-wizard hero
// moments; pages opt in with `dark:` utilities, not a root class.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AddressProvider>
            <App />
          </AddressProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
